import React, { forwardRef } from 'react'
import Icon from 'particles/Icon'
import styled, { css } from 'particles/themes/styled'
import Text from 'particles/Text'
import { ButtonProps, ButtonContainer as BaseButtonContainer, Loader, styledDisabled } from 'particles/Button'

const ButtonContainer = styled(BaseButtonContainer)<ButtonProps>`
    display: inline-flex;
    gap: ${({ theme }): string => theme.spacing[200]};
    min-width: 87px;
    min-height: 22px;
    padding: 2px 12px;
    border-radius: 12px;
    border-style: dashed;
    border-width: 1px;
    border-color: ${({ theme }) => theme.colors.border.action};
    &:hover {
        ${({ theme, color, isDisabled, isLoading }) => ({
            background:
                color === 'primary'
                    ? theme.colors.surface.actionPastelHover
                    : theme.colors.surface.neutralActionPastelHover,
            borderStyle: isDisabled || isLoading ? 'dashed' : 'solid',
        })}
    }
    .button-label,
    .sectionLoader-label {
        font-weight: 400;
    }
    .sectionLoader-icon {
        color: ${({ theme }) => theme.colors.icon.neutralAction};
    }
    ${({ isDisabled }) =>
        isDisabled &&
        css`
            border-color: ${({ theme }) => theme.colors.border.disable};
            ${styledDisabled}
        `}
`

export interface RoundButtonProps extends ButtonProps {
    label?: string
    loadingText?: string
}

const RoundButton = forwardRef<HTMLButtonElement, RoundButtonProps>(
    (
        {
            color = 'primary',
            icon,
            iconForm = 'regular',
            iconFillColor = 'primary',
            label,
            loadingText,
            isDisabled = false,
            isLoading = false,
            ...props
        },
        ref,
    ) => {
        return (
            <ButtonContainer
                ref={ref}
                color={color}
                form={'outlined'}
                isDisabled={isDisabled}
                isLoading={isLoading}
                {...props}
            >
                {isLoading ? (
                    <Loader size={'s'} label={loadingText} spinnerColor="currentColor" />
                ) : (
                    <>
                        {icon && (
                            <Icon
                                className="button-icon"
                                name={icon}
                                form={iconForm}
                                size={'16px'}
                                color="currentColor"
                                fillColor={iconFillColor}
                            />
                        )}
                        <Text className="button-label" scale={'caption400'}>
                            {label}
                        </Text>
                    </>
                )}
            </ButtonContainer>
        )
    },
)

RoundButton.displayName = 'RoundButton'

export default RoundButton
